/**
 * Modern Bold · Favorites
 * ─────────────────────────────────────────────────────────────────────────
 * Two layers:
 *
 *   MBFavorites            the store — owns the lists, persists them, and is
 *                          the only thing that touches storage or the network
 *   <mb-wishlist-button>   the UI — role="button" + aria-pressed, reads the
 *                          store on every change and keeps no state of its own
 *
 * Shape (localStorage `mb_favorites`):
 *   { v: 3, activeId, dirty, lists: [{ id, title, isDefault, items, updatedAt }] }
 *
 *   items   [{ id, handle }] — `id` is the product id and the durable key;
 *           `handle` is a cached convenience. Shopify creates no redirect when
 *           a handle changes, so a handle-only favorite silently dies on
 *           rename; the id survives and is what the card fetch queries by.
 *   dirty   local changes the server has not acknowledged yet
 *
 * Items restored from the v2 release carry a handle and no id. They are
 * upgraded in place the first time something resolves them.
 *
 * `mb_wishlist` (flat handle array) is still mirrored on every change so a
 * rollback to the previous release can still read a migrated browser.
 *
 * Event: document `mb:wishlist-updated`
 *        { detail: { wishlist, lists, activeId } }
 *
 * Signed in, the store syncs to the customer's own metafield through the
 * PM app. It wakes when snippets/modern-wishlist-bootstrap.liquid ships an
 * endpoint, a site key and the customer's key; without them nothing here
 * issues a request. The contract lives in that snippet's header.
 *
 * Note on double-load: classic <script> tags share one lexical environment,
 * and several sections include this file. Everything lives inside the
 * customElements guard so a second run finds the element defined, skips the
 * block, and re-declares nothing.
 */
if (!customElements.get('mb-wishlist-button')) {
  const KEY = 'mb_favorites';
  const MIRROR_KEY = 'mb_wishlist';
  const SYNCED_KEY = 'mb_favorites_synced';
  const PUSH_DELAY = 600;
  const DEFAULT_TITLE = 'My Favorites';
  /* The theme dev server (npm run dev) is the only place these hostnames occur. */
  const LOCAL_DEV = ['127.0.0.1', 'localhost'].includes(location.hostname);
  const LOCAL_ENDPOINT = 'http://127.0.0.1:8787/v1/f';

  const cfg = window.MB_WISHLIST_CONFIG ?? {};

  const readJSON = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
  };
  const writeJSON = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota / private mode */ }
  };

  const escapeHTML = (value) => String(value).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const escapeAttr = escapeHTML;

  const newId = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

  /** A product reference: id when we know it, handle as the fallback key. */
  const toItem = (ref) => {
    const id = Number(ref?.id);
    return { id: Number.isFinite(id) && id > 0 ? id : null, handle: String(ref?.handle ?? '') };
  };
  const isItem = (item) => item.id !== null || item.handle !== '';
  const sameItem = (a, b) => (a.id !== null && b.id !== null
    ? a.id === b.id
    : Boolean(a.handle) && a.handle === b.handle);

  const items = (arr) => {
    const out = [];
    (Array.isArray(arr) ? arr : []).forEach((raw) => {
      const item = toItem(raw);
      if (isItem(item) && !out.some((o) => sameItem(o, item))) out.push(item);
    });
    return out;
  };

  const makeList = (over = {}) => ({
    id: newId(),
    title: DEFAULT_TITLE,
    isDefault: false,
    items: [],
    updatedAt: Date.now(),
    ...over,
  });

  /* Repairs anything storage or the server hands us: exactly one default, an
     activeId that resolves, deduped items. */
  const normalize = (raw) => {
    const lists = (Array.isArray(raw?.lists) ? raw.lists : [])
      .filter((l) => l && typeof l === 'object')
      .map((l) => makeList({
        id: typeof l.id === 'string' ? l.id : newId(),
        title: [l.title, l.label].find((t) => typeof t === 'string' && t) ?? DEFAULT_TITLE,
        isDefault: Boolean(l.isDefault),
        updatedAt: Number(l.updatedAt) || Date.now(),
        // v2 stored a flat handle array; the server sends productIds. Only
        // these fields survive — a legacy key must not ride along, or a stale
        // reader keeps working on migrated data and fails on a new list.
        items: items(l.items ?? l.productIds?.map((id) => ({ id })) ?? l.handles?.map((handle) => ({ handle }))),
      }));
    if (!lists.length) lists.push(makeList({ isDefault: true }));
    // A merge can bring two flagged lists together; the first one keeps the flag.
    lists.slice(lists.findIndex((l) => l.isDefault) + 1).forEach((l) => { l.isDefault = false; });
    if (!lists.some((l) => l.isDefault)) lists[0].isDefault = true;
    const active = lists.find((l) => l.id === raw?.activeId) ?? lists.find((l) => l.isDefault) ?? lists[0];
    return { v: 3, activeId: active.id, dirty: Boolean(raw?.dirty), lists };
  };

  /* Every visit starts on the default list. activeId is the favorites page's
     view state for one visit, not a saved preference — and since the heart
     saves to the default, opening anywhere else would show a list the last
     thing you hearted is not in. */
  const openOnDefault = (next) => {
    next.activeId = (next.lists.find((l) => l.isDefault) ?? next.lists[0]).id;
    return next;
  };

  const load = () => {
    const saved = readJSON(KEY, null);
    if (saved?.v === 3 || saved?.v === 2) return openOnDefault(normalize(saved));
    // Pre-list installs stored a flat handle array under the mirror key.
    const mirror = readJSON(MIRROR_KEY, null);
    const seeded = Array.isArray(mirror) ? mirror.map((handle) => ({ handle })) : [];
    return normalize({ lists: [makeList({ isDefault: true, items: seeded })] });
  };

  let state = load();

  const listById = (id) => state.lists.find((l) => l.id === id);
  /* The heart adds here when no list is named. It is the default list, not the
     active one: `activeId` is the favorites page's viewing state, and a rug
     saved from a collection should not land in whichever list that page
     happened to be left on. */
  const targetList = (id) => listById(id) ?? state.lists.find((l) => l.isDefault) ?? state.lists[0];
  const allItems = () => {
    const out = [];
    state.lists.forEach((l) => l.items.forEach((i) => { if (!out.some((o) => sameItem(o, i))) out.push(i); }));
    return out;
  };
  const allHandles = () => allItems().map((i) => i.handle).filter(Boolean);

  const commit = ({ push = true } = {}) => {
    state.dirty = state.dirty || push;
    writeJSON(KEY, state);
    writeJSON(MIRROR_KEY, allHandles());
    document.dispatchEvent(new CustomEvent('mb:wishlist-updated', {
      detail: { wishlist: allHandles(), lists: state.lists, activeId: state.activeId },
    }));
    if (push) schedulePush();
  };

  const touch = (list) => { list.updatedAt = Date.now(); };
  const findIn = (list, item) => list.items.find((i) => sameItem(i, item));

  /* Callers know the product by whichever key they had to hand — the ⋯ menu
     only has an id. Top the reference up from what is already stored, so
     moving a rug between lists cannot drop the handle we already knew. */
  const enrich = (item) => {
    for (const list of state.lists) {
      const hit = findIn(list, item);
      if (hit) return { id: item.id ?? hit.id, handle: item.handle || hit.handle };
    }
    return item;
  };

  const MBFavorites = {
    get lists() { return state.lists.map((l) => ({ ...l, items: l.items.map((i) => ({ ...i })) })); },
    get activeId() { return state.activeId; },

    /** Items in one list, or across every list when listId is omitted. */
    items(listId) {
      const src = listId ? (listById(listId)?.items ?? []) : allItems();
      return src.map((i) => ({ ...i }));
    },

    /** Product ids only — what the card fetch queries by. */
    ids(listId) { return this.items(listId).map((i) => i.id).filter((id) => id !== null); },

    /** Handles only — the mirror, and the backfill's input. */
    handles(listId) { return this.items(listId).map((i) => i.handle).filter(Boolean); },

    /** Saved anywhere, or in one specific list. */
    has(ref, listId) {
      const item = toItem(ref);
      if (!isItem(item)) return false;
      return listId ? Boolean(findIn(listById(listId) ?? { items: [] }, item))
                    : state.lists.some((l) => findIn(l, item));
    },

    /**
     * Fills in an id (or a handle) the store did not have yet, matching on
     * whichever key it does have. This is how v2 handle-only items become
     * id-backed once something resolves them.
     */
    upgrade(ref) {
      const item = toItem(ref);
      if (!isItem(item)) return false;
      let changed = false;
      state.lists.forEach((list) => {
        const hit = findIn(list, item);
        if (!hit) return;
        if (hit.id === null && item.id !== null) { hit.id = item.id; changed = true; }
        if (!hit.handle && item.handle) { hit.handle = item.handle; changed = true; }
      });
      if (changed) commit({ push: false });
      return changed;
    },

    add(ref, listId) {
      const item = enrich(toItem(ref));
      const list = targetList(listId);
      if (!isItem(item) || findIn(list, item)) return false;
      list.items.push(item);
      touch(list);
      commit();
      return true;
    },

    /** Removes from one list, or from every list when listId is omitted. */
    remove(ref, listId) {
      const item = toItem(ref);
      if (!isItem(item)) return false;
      const scope = listId ? [listById(listId)].filter(Boolean) : state.lists;
      let hit = false;
      scope.forEach((list) => {
        const i = list.items.findIndex((x) => sameItem(x, item));
        if (i === -1) return;
        list.items.splice(i, 1);
        touch(list);
        hit = true;
      });
      if (hit) commit();
      return hit;
    },

    /** Returns the resulting saved state. */
    toggle(ref, listId) {
      return this.has(ref, listId) ? (this.remove(ref, listId), false) : this.add(ref, listId);
    },

    createList(title) {
      const list = makeList({ title: (title || DEFAULT_TITLE).trim() });
      state.lists.push(list);
      state.activeId = list.id;
      commit();
      return list.id;
    },

    renameList(id, title) {
      const list = listById(id);
      if (!list || !title?.trim()) return false;
      list.title = title.trim();
      touch(list);
      commit();
      return true;
    },

    /** The last list is never removed — the heart always needs a home. */
    deleteList(id) {
      if (state.lists.length < 2) return false;
      const i = state.lists.findIndex((l) => l.id === id);
      if (i === -1) return false;
      const [gone] = state.lists.splice(i, 1);
      if (gone.isDefault) state.lists[0].isDefault = true;
      if (state.activeId === id) state.activeId = state.lists.find((l) => l.isDefault).id;
      commit();
      return true;
    },

    setActive(id) {
      if (!listById(id)) return false;
      state.activeId = id;
      commit({ push: false });
      return true;
    },

    /** Puts the product in exactly one list. One commit, so a single event. */
    moveTo(ref, listId) {
      // Enrich before the splices below remove the record we are reading from.
      const item = enrich(toItem(ref));
      const to = listById(listId);
      if (!isItem(item) || !to) return false;
      const existing = findIn(to, item);
      state.lists.forEach((list) => {
        if (list === to) return;
        const i = list.items.findIndex((x) => sameItem(x, item));
        if (i !== -1) { list.items.splice(i, 1); touch(list); }
      });
      if (!existing) { to.items.push(item); touch(to); }
      commit();
      return true;
    },
  };

  window.MBFavorites = MBFavorites;

  /* ── Customer sync ──────────────────────────────────────────────────────
     Inert without the bootstrap's endpoint, site key, customer and key: every
     function below returns immediately, so a signed-out page never opens a
     connection. A 403 means the key is not this customer's; nothing on this
     page load can repair that, so it stops trying rather than retrying on
     every tap. */
  let refused = false;
  const canSync = () => !refused && Boolean(cfg.endpoint && cfg.siteKey && cfg.customerId && cfg.key);
  const endpoint = () => (LOCAL_DEV ? LOCAL_ENDPOINT : cfg.endpoint);
  let pushTimer = null;

  /* `label` and `productIds` are the names the PM stores and the bootstrap
     reads back. Items with no id yet are left out — an un-backfilled handle
     has no product to point at. */
  const payload = () => ({
    sk: cfg.siteKey,
    customerId: cfg.customerId,
    key: cfg.key,
    lists: state.lists.map(({ id, title, updatedAt, items: list }) => ({
      id,
      label: title,
      productIds: list.map((i) => i.id).filter((v) => v !== null),
      updatedAt,
    })),
  });

  const schedulePush = () => {
    if (!canSync()) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(push, PUSH_DELAY);
  };

  /* Sends the whole state, not a delta — the write is idempotent, so a lost
     request costs nothing and the next change repairs it. text/plain keeps
     the cross-site POST free of a preflight; the PM reads the bytes as JSON. */
  const push = async () => {
    if (!canSync()) return;
    try {
      const res = await fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        credentials: 'omit',
        body: JSON.stringify(payload()),
      });
      if (res.status === 403) refused = true;
      if (!res.ok) throw new Error(`favorites sync ${res.status}`);
      state.dirty = false;
      commit({ push: false });
    } catch (err) {
      // Local stays authoritative; the next change retries.
      console.warn('[favorites] sync failed, keeping the local copy', err);
    }
  };

  /* Union by list id, then by title — the anonymous "My Favorites" and the
     customer's own "My Favorites" are the same list, not two. */
  const unionLists = (local, remote) => {
    const merged = remote.map((l) => ({ ...l, items: items(l.items) }));
    const match = (l) => merged.find((m) => (l.id && m.id === l.id)
      || m.title.trim().toLowerCase() === l.title.trim().toLowerCase());
    local.forEach((l) => {
      const hit = match(l);
      if (hit) hit.items = items([...hit.items, ...l.items]);
      else merged.push({ ...l, items: items(l.items) });
    });
    return merged;
  };

  /* The server stores ids only; handles this device already knew survive the
     swap, so the mirror and handle-only references keep working. */
  const adopt = (lists, { push: shouldPush }) => {
    const known = state.lists.flatMap((l) => l.items);
    const next = normalize({ lists, activeId: state.activeId, dirty: shouldPush });
    next.lists.forEach((l) => l.items.forEach((item) => {
      if (!item.handle) item.handle = known.find((k) => k.id !== null && k.id === item.id)?.handle ?? '';
    }));
    state = next;
    commit({ push: shouldPush });
  };

  const mergeWithCustomer = () => {
    if (!canSync()) return;
    const remote = normalize({ lists: Array.isArray(cfg.lists) ? cfg.lists : [] }).lists;
    let syncedWith = null;
    try { syncedWith = localStorage.getItem(SYNCED_KEY); } catch { /* private mode */ }
    const customerId = String(cfg.customerId);

    if (syncedWith === customerId) {
      // Already merged here. The customer's own copy wins unless this device
      // has changes the server has not seen — otherwise a list deleted on
      // another device would come back every time this one loads.
      adopt(state.dirty ? unionLists(state.lists, remote) : remote, { push: state.dirty });
      return;
    }

    try { localStorage.setItem(SYNCED_KEY, customerId); } catch { /* private mode */ }

    if (syncedWith) {
      // A different customer used this browser — their lists are not ours.
      adopt(remote, { push: false });
      return;
    }

    // First sign-in on this device: the anonymous list has to survive it.
    adopt(unionLists(state.lists, remote), { push: true });
  };

  // A pending debounce would be lost to navigation, so flush it on the way out.
  addEventListener('pagehide', () => {
    if (!canSync() || !state.dirty) return;
    clearTimeout(pushTimer);
    try {
      navigator.sendBeacon(endpoint(), new Blob([JSON.stringify(payload())], { type: 'text/plain' }));
    } catch { /* best effort */ }
  });

  mergeWithCustomer();

  class MBWishlistButton extends HTMLElement {
    #ref = null;

    #onClick = (event) => {
      // The heart sits over the card's photo link — without this the click
      // navigates to the product instead of toggling.
      event.preventDefault();
      event.stopPropagation();
      this.#activate();
    };

    #onKeydown = (event) => {
      // Enter and Space are the activation keys for role="button". e.code is a
      // fallback for layouts that report Space as something other than ' '.
      if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
        event.preventDefault();
        this.#activate();
      }
    };

    #onUpdate = () => this.#render();

    connectedCallback() {
      this.#ref = { id: this.dataset.productId, handle: this.dataset.productHandle };
      if (!this.#ref.id && !this.#ref.handle) return;

      // Defensive — Liquid sets these, but a hand-written instance may not.
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

      this.#render();
      this.addEventListener('click', this.#onClick);
      this.addEventListener('keydown', this.#onKeydown);
      // Every heart for this product follows the store, so the card and the
      // PDP never disagree about what is saved.
      document.addEventListener('mb:wishlist-updated', this.#onUpdate);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('keydown', this.#onKeydown);
      document.removeEventListener('mb:wishlist-updated', this.#onUpdate);
    }

    /* One tap, always: saves to the last-used list or removes. Organising
       into lists happens on the favorites page, never at the moment of
       saving — nobody should have to pick a list to keep a rug. */
    #activate() { MBFavorites.toggle(this.#ref); }

    #render() {
      const saved = MBFavorites.has(this.#ref);
      this.setAttribute('aria-pressed', String(saved));
      const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.setAttribute('aria-label', label);
    }
  }

  /* Header badge. Renders only the number — where it sits (over the heart's
     corner, or inline in the drawer) is the stylesheet's call. Owns the
     link's accessible name so the count is announced once, as part of
     "Favorites, 3 saved", instead of twice. */
  class MBFavoritesCount extends HTMLElement {
    #onUpdate = () => this.#render();

    connectedCallback() {
      this.setAttribute('aria-hidden', 'true');
      this.#render();
      document.addEventListener('mb:wishlist-updated', this.#onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('mb:wishlist-updated', this.#onUpdate);
    }

    #render() {
      const count = MBFavorites.items().length;
      this.hidden = count === 0;
      this.textContent = count ? String(count) : '';
      this.closest('a')?.setAttribute('aria-label', count ? `Favorites, ${count} saved` : 'Favorites, empty');
    }
  }

  /* List picker, opened by the favorites page's per-card ⋯ button —
     popover="auto" buys light dismiss, Escape and the top layer, so this only
     has to own its content, its position and the focus handoff. */
  class MBFavoritesMenu extends HTMLElement {
    #ref = null;
    #anchor = null;
    #creating = false;
    #title = 'Save to list';
    #showRemove = true;
    #frame = 0;
    #pressedAnchor = null;

    #onToggle = (event) => {
      // Toggle events are queued, so a close can land after a press on another
      // card has already reopened us against a new anchor. Acting on it then
      // would clear the live button's state and steal focus out of the menu.
      if (event.newState === 'closed' && this.matches(':popover-open')) return;
      this.#anchor?.setAttribute('aria-expanded', String(event.newState === 'open'));
      // The favorites page holds its re-render while this is open, otherwise
      // toggling a list would rip the anchor out from under the popover. It
      // needs the handle to know which heart to hand focus back to, because
      // its re-render lands before the focus restore below and detaches ours.
      document.dispatchEvent(new CustomEvent('mb:favorites-menu', {
        detail: { open: event.newState === 'open', ref: this.#ref },
      }));
      if (event.newState === 'open') {
        addEventListener('resize', this.#onResize, { passive: true });
        this.querySelector('[role="menuitemradio"]')?.focus();
      } else {
        this.#untrack();
        this.#creating = false;
        // Never let the dismissal drop focus to <body>. The menu rides the page
        // scroll, so its button may be off-screen by now — restoring focus must
        // not yank the page back to it.
        const r = this.#anchor?.isConnected ? this.#anchor.getBoundingClientRect() : null;
        if (r) this.#anchor.focus({ preventScroll: r.bottom <= 0 || r.top >= innerHeight });
      }
    };

    /* Scrolling needs no handler — the box is absolute, so the page carries it.
       Only the viewport-dependent parts, the edge clamp and the above/below
       flip, go stale, and only on resize. */
    #onResize = () => {
      this.#frame ||= requestAnimationFrame(() => {
        this.#frame = 0;
        if (this.matches(':popover-open') && this.#anchor?.isConnected) this.#position();
      });
    };

    #untrack() {
      removeEventListener('resize', this.#onResize);
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }

    #onUpdate = () => { if (this.matches(':popover-open')) this.#render(); };

    #onKeydown = (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-fav-menu-input]')) {
        event.preventDefault();
        this.#create();
        return;
      }
      const items = [...this.querySelectorAll('[role="menuitemradio"], [data-fav-menu-action]')];
      const i = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? i + 1 : i - 1;
        items[(next + items.length) % items.length]?.focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        (event.key === 'Home' ? items[0] : items.at(-1))?.focus();
      }
    };

    connectedCallback() {
      this.setAttribute('popover', 'auto');
      this.setAttribute('role', 'menu');
      this.className = 'mb-fav-menu';
      this.addEventListener('toggle', this.#onToggle);
      this.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('click', this.#onClick);
      document.addEventListener('mb:wishlist-updated', this.#onUpdate);
      document.addEventListener('pointerdown', this.#onPointerDown, { capture: true });
    }

    disconnectedCallback() {
      this.removeEventListener('toggle', this.#onToggle);
      this.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('click', this.#onClick);
      document.removeEventListener('mb:wishlist-updated', this.#onUpdate);
      document.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
      this.#untrack();
    }

    /* An auto popover light-dismisses on pointerdown, so a press on its own
       button reaches the click handler with the menu already closed and would
       reopen it. Catch the press while the menu is still open; every press
       rewrites this, so a stale value can never swallow a later one. */
    #onPointerDown = (event) => {
      this.#pressedAnchor = this.matches(':popover-open') && this.#anchor?.contains(event.target)
        ? this.#anchor
        : null;
    };

    /* The favorites page opens this from a labelled "Move to list" button
       and carries its own remove control, so it drops the remove row. */
    open(ref, anchor, { title = 'Save to list', remove = true } = {}) {
      // That press already closed us — it was the close half of a toggle.
      const toggledOff = this.#pressedAnchor === anchor;
      this.#pressedAnchor = null;
      if (toggledOff) return;
      // Keyboard activation never light-dismisses, so we can still be open here
      // and showPopover() would throw.
      if (this.matches(':popover-open')) {
        const sameButton = this.#anchor === anchor;
        this.hidePopover();
        if (sameButton) return;
      }
      // Moving to another card: the outgoing button's close event either already
      // fired against the old anchor or is queued behind this swap, so reset it
      // here rather than leaving two buttons claiming to be expanded.
      if (this.#anchor !== anchor) this.#anchor?.setAttribute('aria-expanded', 'false');
      this.#ref = ref;
      this.#anchor = anchor;
      this.#creating = false;
      this.#title = title;
      this.#showRemove = remove;
      this.#render();
      this.showPopover();
      this.#position();
    }

    #onClick = (event) => {
      const row = event.target.closest('[role="menuitemradio"]');
      if (row) { this.#choose(row.dataset.listId); return; }

      const action = event.target.closest('[data-fav-menu-action]')?.dataset.favMenuAction;
      if (action === 'new') {
        this.#creating = true;
        this.#render();
        this.querySelector('[data-fav-menu-input]')?.focus();
      } else if (action === 'create') {
        this.#create();
      } else if (action === 'cancel') {
        this.#creating = false;
        this.#render();
        this.querySelector('[data-fav-menu-action="new"]')?.focus();
      } else if (action === 'remove') {
        MBFavorites.remove(this.#ref);
        this.hidePopover();
      }
    };

    /* A rug lives in one list: picking one moves it there, and the menu
       closes because the choice is the whole job. Focus goes back to the
       heart through the toggle handler. */
    #choose(listId) {
      MBFavorites.moveTo(this.#ref, listId);
      this.hidePopover();
    }

    #create() {
      const input = this.querySelector('[data-fav-menu-input]');
      const title = input?.value.trim();
      if (!title) { input?.focus(); return; }
      this.#choose(MBFavorites.createList(title));
    }

    #position(r = this.#anchor.getBoundingClientRect()) {
      const { width, height } = this.getBoundingClientRect();
      const gap = 8;
      const left = Math.min(Math.max(gap, r.right - width), innerWidth - width - gap);
      const below = r.bottom + gap;
      const top = below + height > innerHeight - gap ? Math.max(gap, r.top - height - gap) : below;
      // Placed in document space: the box is absolute, so it rides the scroll.
      this.style.left = `${left + scrollX}px`;
      this.style.top = `${top + scrollY}px`;
    }

    #render() {
      const rows = MBFavorites.lists.map((list) => `
        <button type="button" role="menuitemradio" class="mb-fav-menu__item"
                aria-checked="${MBFavorites.has(this.#ref, list.id)}" data-list-id="${escapeAttr(list.id)}">
          <span class="mb-fav-menu__tick" aria-hidden="true"></span>
          <span class="mb-fav-menu__name">${escapeHTML(list.title)}</span>
          <span class="mb-fav-menu__count" aria-hidden="true">${list.items.length}</span>
        </button>`).join('');

      const creator = this.#creating ? `
        <div class="mb-fav-menu__new">
          <label class="visually-hidden" for="mb-fav-menu-input">New list name</label>
          <input id="mb-fav-menu-input" type="text" class="mb-fav-menu__input" data-fav-menu-input
                 maxlength="60" placeholder="List name" autocomplete="off">
          <button type="button" class="mb-fav-menu__save" data-fav-menu-action="create">Create</button>
          <button type="button" class="mb-fav-menu__cancel" data-fav-menu-action="cancel">Cancel</button>
        </div>` : `
        <button type="button" class="mb-fav-menu__add" data-fav-menu-action="new">
          <span aria-hidden="true">+</span> New list
        </button>`;

      const removal = this.#showRemove && MBFavorites.has(this.#ref) ? `
        <button type="button" class="mb-fav-menu__remove" data-fav-menu-action="remove">Remove from favorites</button>` : '';

      this.innerHTML = `<p class="mb-fav-menu__title">${escapeHTML(this.#title)}</p>${rows}${creator}${removal}`;
    }
  }

  customElements.define('mb-wishlist-button', MBWishlistButton);
  customElements.define('mb-favorites-count', MBFavoritesCount);
  customElements.define('mb-favorites-menu', MBFavoritesMenu);
}
